import { useState, useMemo, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueries } from "@tanstack/react-query";
import LayoutWide from "../layouts/LayoutWide.jsx";
import useTimezone from "../hooks/useTimezone.js";
import { formatDate, formatChartAxisLabel, formatChartTooltip } from "../utility/datetime.js";
import Spinner from "../components/Spinner.jsx";
import Gauge from "../components/Gauge.jsx";
import GrowPhaseBar, { PHASE_COLORS } from "../components/GrowPhaseBar.jsx";
import MetricChart from "../components/MetricChart.jsx";
import ReactECharts from "echarts-for-react";
import { computeGrowMarks, activeEventTypes, EVENT_META } from "../components/GrowPhaseOverlay.jsx";
import GrowNotesFeed from "../components/GrowNotesFeed.jsx";
import HarvestLog from "../components/HarvestLog.jsx";
import { computeVPDSeries, computeVPD } from "../utility/vpd.js";
import { useMetricQuery } from "../hooks/useMetricQuery.js";
import { useTheme } from "../hooks/useTheme.js";
import TimeRangePicker from "../components/TimeRangePicker.jsx";
import {
  useGrowQuery,
  useGrowPhasesQuery,
  useGrowNotesQuery,
  useGrowHarvestsQuery,
  useAdvancePhase,
  useCompleteGrow,
  useUpdateGrow,
  useUpdateGrowPhase,
  useGrowPhaseSummariesQuery,
  useStabilityScore,
} from "../hooks/useGrowsQuery.js";
import StabilityScore from "../components/StabilityScore.jsx";
import GrowReferenceToggle, { useGrowReference } from "../components/GrowReferenceToggle.jsx";
import InlineEdit from "../components/InlineEdit.jsx";
import { PencilIcon, CheckIcon, XMarkIcon } from "@heroicons/react/20/solid";

const API = import.meta.env.VITE_API_BASE_URL;

const PHASE_SEQUENCE = ["Inoculation", "Colonization", "Pinning", "Fruiting", "Harvesting"];

function nextPhase(currentPhaseName) {
  const idx = PHASE_SEQUENCE.indexOf(currentPhaseName);
  if (idx === -1 || idx === PHASE_SEQUENCE.length - 1) return null;
  return PHASE_SEQUENCE[idx + 1];
}

const STATUS_COLORS = {
  active:    "bg-green-100 text-green-800",
  completed: "bg-gray-100 text-gray-600",
  abandoned: "bg-red-100 text-red-700",
};

// Returns hostname + label + sensorLabels + live sensor values + thresholds
function useDashboardDevice(deviceId) {
  return useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const res = await fetch(`${API}dashboard`, { credentials: "include" });
      if (!res.ok) throw new Error("dashboard fetch failed");
      return res.json();
    },
    staleTime: 60_000,
    enabled: !!deviceId,
    select: (data) => {
      const d = data.devices?.[deviceId];
      if (!d) return null;
      const sensorLabels = {};
      const sensorValues = {}; // { sensor_code: { temperature: val, humidity: val, co2: val } }
      for (const s of (d.sensors ?? [])) {
        sensorLabels[s.sensor_code] = s.display_name || s.sensor_name || s.sensor_code;
        sensorValues[s.sensor_code] = {};
        for (const m of (s.metrics ?? [])) {
          sensorValues[s.sensor_code][m.metric] = m.latest_value;
        }
      }
      return {
        hostname: d.device.hostname,
        label: d.device.display_name ?? d.device.hostname,
        sensorLabels,
        sensorValues,
        thresholds: d.thresholds?.thresholds ?? {},
        sensors: d.sensors ?? [],
      };
    },
  });
}

// Renders a VPD chart computed from the fetched temp+humidity series (no extra API call).
function VPDChart({ hostname, timeRange, marks, darkMode = false, timeZone }) {
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data: tempData } = useMetricQuery({ metric: "temperature", hostname, ...timeRange });
  const { data: humData }  = useMetricQuery({ metric: "humidity",    hostname, ...timeRange });

  const vpdSeries = useMemo(() => {
    const tempPoints = tempData?.[0]?.points ?? [];
    const humPoints  = humData?.[0]?.points  ?? [];
    return computeVPDSeries(tempPoints, humPoints);
  }, [tempData, humData]);

  const axisColor = darkMode ? "#4b5563" : "#6b7280";
  const gridColor = darkMode ? "#4b5563" : "#e5e7eb";
  const textColor = darkMode ? "#9ca3af" : "#374151";

  const option = useMemo(() => ({
    animation: false,
    grid: { top: 36, right: 16, bottom: 36, left: 56, containLabel: false },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross", animation: false },
      formatter: (params) => {
        const ts = formatChartTooltip(params[0].axisValue, tz);
        return `${ts}<br/>VPD: <b>${params[0].data[1].toFixed(3)} kPa</b>`;
      },
    },
    xAxis: {
      type: "time",
      axisLabel: {
        formatter: (val) => formatChartAxisLabel(val, tz, timeRange.end - timeRange.start),
        hideOverlap: true,
        fontSize: 11,
        color: textColor,
      },
      axisLine: { lineStyle: { color: axisColor } },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      name: "kPa",
      nameLocation: "end",
      nameTextStyle: { color: textColor, fontSize: 11, padding: [0, 0, 0, -40] },
      axisLabel: { formatter: "{value}", fontSize: 11, color: textColor },
      axisLine: { lineStyle: { color: axisColor } },
      splitLine: { lineStyle: { type: "dashed", color: gridColor } },
    },
    series: [{
      name: "VPD",
      type: "line",
      smooth: true,
      symbol: "none",
      lineStyle: { color: "#06b6d4", width: 2 },
      itemStyle: { color: "#06b6d4" },
      areaStyle: { color: "#06b6d4", opacity: 0.08 },
      data: vpdSeries.map(p => [p.t, p.v]),
      connectNulls: false,
      ...marks,
    }],
  }), [vpdSeries, marks, axisColor, gridColor, textColor]);

  if (!hostname) return null;

  return (
    <div className={`${darkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'} rounded-lg p-4 flex flex-col gap-2`}>
      <h3 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>VPD</h3>
      {vpdSeries.length === 0 ? (
        <div className="flex items-center justify-center h-40 text-gray-500 text-sm">No data</div>
      ) : (
        <ReactECharts
          option={option}
          style={{ height: 200 }}
          opts={{ renderer: "canvas" }}
          notMerge
          onChartReady={(chart) => { chart.group = "grow-detail"; }}
        />
      )}
    </div>
  );
}

export default function GrowDetail() {
  const [theme] = useTheme();
  const darkMode = theme === "dark";
  const tz = useTimezone();
  const { growId } = useParams();

  const { data: grow, isLoading: growLoading, isError: growError } = useGrowQuery(growId);
  const { data: phases = [], isLoading: phasesLoading } = useGrowPhasesQuery(growId);
  const { data: notes = [] } = useGrowNotesQuery(growId);
  const { data: harvests = [] } = useGrowHarvestsQuery(growId);
  const { data: phaseSummaries = [] } = useGrowPhaseSummariesQuery(growId);
  const advancePhase = useAdvancePhase(growId);
  const completeGrow = useCompleteGrow(growId);
  const updateGrow = useUpdateGrow(growId);
  const updateGrowPhase = useUpdateGrowPhase(growId);

  const { data: device, isLoading: deviceLoading } = useDashboardDevice(grow?.device_id);

  // Live gauge values patched by SSE
  const [liveValues, setLiveValues] = useState(null);
  const sseStartedRef = useRef(false);

  const currentPhaseForScore = phases.find(p => !p.ended_at) ?? null;
  const stabilityScore = useStabilityScore({ hostname: device?.hostname, currentPhase: currentPhaseForScore });

  const reference = useGrowReference(growId);

  const [advanceError, setAdvanceError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [completeNote, setCompleteNote] = useState("");
  const [completeError, setCompleteError] = useState(null);
  const [timeRange, setTimeRange] = useState(null); // set from grow dates in useEffect
  const [enabledTypes, setEnabledTypes] = useState(null);
  const [editPhaseId, setEditPhaseId] = useState(null);
  const [phaseEditDeviceId, setPhaseEditDeviceId] = useState("");
  const [startedAtEdit, setStartedAtEdit] = useState(false);
  const [startedAtValue, setStartedAtValue] = useState("");
  const [startedAtError, setStartedAtError] = useState(null);

  // Initialize time range from grow dates once grow loads
  useEffect(() => {
    if (!grow) return;
    const start = new Date(grow.started_at).getTime();
    const end = grow.ended_at ? new Date(grow.ended_at).getTime() : Date.now();
    setTimeRange({ start, end });
  }, [grow?.id]); // only re-run when grow ID changes, not on every re-render

  // SSE: patch live sensor values
  useEffect(() => {
    if (!device?.hostname || sseStartedRef.current) return;
    sseStartedRef.current = true;
    const es = new EventSource(`${API}dashboardlive`, { withCredentials: true });
    es.addEventListener("metrics", (e) => {
      const updates = JSON.parse(e.data);
      const update = updates.find(u => u.device_id === grow?.device_id);
      if (!update) return;
      const next = {};
      for (const su of update.sensors) {
        next[su.sensor_code] = {};
        for (const mu of su.metrics) {
          next[su.sensor_code][mu.metric] = mu.latest_value;
        }
      }
      setLiveValues(next);
    });
    return () => { es.close(); sseStartedRef.current = false; };
  }, [device?.hostname]);

  const presentTypes = useMemo(() => activeEventTypes(notes), [notes]);

  const enabledSet = useMemo(() => {
    if (enabledTypes === null) return new Set(presentTypes);
    return enabledTypes;
  }, [enabledTypes, presentTypes]);

  function toggleType(type) {
    const next = new Set(enabledSet);
    if (next.has(type)) next.delete(type); else next.add(type);
    setEnabledTypes(next);
  }

  const marks = useMemo(
    () => computeGrowMarks(phases, notes, enabledSet),
    [phases, notes, enabledSet]
  );

  // Clamp time range to grow floor/ceiling
  function handleRangeChange(range) {
    if (!grow) return setTimeRange(range);
    const floor = new Date(grow.started_at).getTime();
    const ceiling = grow.ended_at ? new Date(grow.ended_at).getTime() : Date.now();
    setTimeRange({
      start: Math.max(range.start, floor),
      end: Math.min(range.end, ceiling),
    });
  }

  // Dashboard device options for phase device selector (must be before early returns)
  const deviceOptions = useMemo(() => {
    if (!device || !grow) return [];
    return [{ id: grow?.device_id, label: device.label }];
  }, [device, grow?.device_id]);

  // Compute unique hostnames per phase for multi-device chart queries
  const phaseHostnames = useMemo(() => {
    if (!phases.length || !device) return [];
    const seen = new Set();
    if (device.hostname) seen.add(device.hostname);
    return Array.from(seen);
  }, [phases, device]);

  // Multi-device metric queries using useQueries (rules-of-hooks safe)
  const METRICS = ["temperature", "humidity", "co2"];
  const multiQueries = useQueries({
    queries: timeRange
      ? phaseHostnames.flatMap(hostname =>
          METRICS.map(metric => ({
            queryKey: ["metric", metric, hostname, timeRange.start, timeRange.end],
            queryFn: async () => {
              const params = new URLSearchParams({
                metric,
                hostname,
                start: Math.floor(timeRange.start / 1000).toString(),
                end: Math.floor(timeRange.end / 1000).toString(),
              });
              const res = await fetch(`${API}metrics/range?${params}`, { credentials: "include" });
              if (!res.ok) throw new Error(`metrics/range ${res.status}`);
              return res.json();
            },
            staleTime: 60_000,
          }))
        )
      : [],
  });

  if (growLoading || phasesLoading) return <LayoutWide title="Grow"><Spinner /></LayoutWide>;
  if (growError || !grow) {
    return (
      <LayoutWide title="Grow">
        <div className="p-4 text-red-600">Grow not found.</div>
      </LayoutWide>
    );
  }

  const currentPhase = phases.find(p => !p.ended_at) ?? null;
  const next = currentPhase ? nextPhase(currentPhase.phase_name) : null;
  const isActive = grow.status === "active";
  const isCompleted = grow.status === "completed";

  // Merged sensor values: SSE patch wins over dashboard snapshot
  const effectiveValues = liveValues ?? device?.sensorValues ?? {};

  async function handleComplete() {
    setCompleteError(null);
    try {
      await completeGrow.mutateAsync(completeNote);
      setShowComplete(false);
      setCompleteNote("");
      setCompleteError(null);
    } catch (err) {
      setCompleteError(err.message ?? "Failed to complete grow");
    }
  }

  async function handleAdvance() {
    if (!next) return;
    setAdvanceError(null);
    try {
      await advancePhase.mutateAsync(next);
      setShowConfirm(false);
    } catch (err) {
      setAdvanceError(err.message ?? "Failed to advance phase");
    }
  }

  async function handleSaveStartedAt() {
    setStartedAtError(null);
    const parsed = new Date(startedAtValue);
    if (isNaN(parsed.getTime())) { setStartedAtError("Invalid date"); return; }
    if (parsed > new Date()) { setStartedAtError("Cannot be in the future"); return; }
    try {
      await updateGrow.mutateAsync({ started_at: parsed.toISOString() });
      setStartedAtEdit(false);
      // Re-initialize time range floor
      setTimeRange(prev => prev ? { ...prev, start: parsed.getTime() } : prev);
    } catch (err) {
      setStartedAtError(err.message ?? "Failed to save");
    }
  }

  async function handleSavePhaseDevice(phase) {
    const deviceId = phaseEditDeviceId === "" ? null : parseInt(phaseEditDeviceId, 10);
    try {
      await updateGrowPhase.mutateAsync({ phaseId: phase.id, device_id: deviceId });
      setEditPhaseId(null);
    } catch (err) {
      // error surfaced inline
    }
  }


  return (
    <LayoutWide title={grow.name}>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-4 text-sm text-gray-400">
        <Link to="/grows" className="hover:text-gray-600 transition-colors">Grows</Link>
        <span>/</span>
        <span className="text-gray-700 font-medium">{grow.name}</span>
      </div>

      {/* Header card */}
      <div className="bg-white rounded-xl shadow p-5 mb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="text-2xl font-semibold text-cyan-800">
                <InlineEdit
                  value={grow.name}
                  placeholder="Grow name"
                  onSave={(name) => updateGrow.mutate({ name, status: grow.status })}
                  className="text-2xl font-semibold text-cyan-800"
                />
              </h1>
              <span className={`text-xs rounded-full px-2.5 py-0.5 font-medium ${STATUS_COLORS[grow.status] ?? STATUS_COLORS.active}`}>
                {grow.status}
              </span>
            </div>
            {grow.species && (
              <span className="inline-block text-sm bg-cyan-50 text-cyan-700 border border-cyan-200 rounded-full px-3 py-0.5">
                {grow.species}
              </span>
            )}
          </div>

          {isActive && next && !showConfirm && (
            <button
              onClick={() => { setShowConfirm(true); setShowComplete(false); }}
              className="shrink-0 text-sm bg-cyan-600 text-white rounded-lg px-4 py-2 hover:bg-cyan-700 transition-colors font-medium"
            >
              Advance to {next} →
            </button>
          )}

          {isActive && next && showConfirm && (
            <div className="flex flex-col items-end gap-2 shrink-0">
              <div className="text-sm text-gray-600">
                Move from <strong>{currentPhase.phase_name}</strong> to <strong>{next}</strong>?
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAdvance}
                  disabled={advancePhase.isPending}
                  className="text-sm bg-cyan-600 text-white rounded px-3 py-1.5 hover:bg-cyan-700 transition-colors disabled:opacity-50"
                >
                  {advancePhase.isPending ? "Advancing…" : "Confirm"}
                </button>
                <button
                  onClick={() => setShowConfirm(false)}
                  className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5"
                >
                  Cancel
                </button>
              </div>
              {advanceError && <div className="text-xs text-red-600">{advanceError}</div>}
            </div>
          )}

          {isActive && !showComplete && (
            <button
              onClick={() => { setShowComplete(true); setShowConfirm(false); }}
              className="shrink-0 text-sm bg-gray-100 text-gray-700 border border-gray-300 rounded-lg px-4 py-2 hover:bg-gray-200 transition-colors font-medium"
            >
              Complete Grow
            </button>
          )}

          {isActive && showComplete && (
            <div className="flex flex-col gap-2 shrink-0 max-w-sm">
              <div className="text-sm text-amber-700 bg-amber-100 border border-amber-200 rounded px-3 py-2">
                Have you logged your final harvest? (optional but recommended)
              </div>
              <textarea
                value={completeNote}
                onChange={e => setCompleteNote(e.target.value)}
                placeholder="Completion note (required)…"
                rows={2}
                className="text-sm border border-gray-300 rounded px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-gray-400"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleComplete}
                  disabled={!completeNote.trim() || completeGrow.isPending}
                  className="text-sm bg-gray-700 text-white rounded px-3 py-1.5 hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {completeGrow.isPending ? "Completing…" : "Complete Grow"}
                </button>
                <button
                  onClick={() => { setShowComplete(false); setCompleteNote(""); setCompleteError(null); }}
                  className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5"
                >
                  Cancel
                </button>
              </div>
              {completeError && <div className="text-xs text-red-600">{completeError}</div>}
            </div>
          )}
        </div>

        <div className="mt-4 flex items-end gap-4">
          <div className="flex-1">
            <GrowPhaseBar phases={phases} showLabel />
          </div>
          <StabilityScore score={stabilityScore} size="md" />
        </div>

        {phases.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-3">
            {phases.map(p => (
              <div key={p.id} className="flex items-center gap-1.5 text-xs text-gray-500">
                <span className={`w-2.5 h-2.5 rounded-sm ${PHASE_COLORS[p.phase_name] ?? "bg-gray-300"}`} />
                {p.phase_name}
                {!p.ended_at && <span className="text-cyan-500 font-medium">(current)</span>}
                <button
                  onClick={() => { setEditPhaseId(p.id); setPhaseEditDeviceId(p.device_id?.toString() ?? ""); }}
                  className="opacity-40 hover:opacity-100 transition-opacity"
                  title="Edit phase device"
                >
                  <PencilIcon className="size-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Phase device edit panel */}
        {editPhaseId != null && (() => {
          const phase = phases.find(p => p.id === editPhaseId);
          if (!phase) return null;
          return (
            <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm flex flex-wrap items-center gap-3">
              <span className="font-medium text-gray-700">{phase.phase_name} — device:</span>
              <select
                value={phaseEditDeviceId}
                onChange={e => setPhaseEditDeviceId(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400"
              >
                <option value="">Use grow default</option>
                {deviceOptions.map(d => (
                  <option key={d.id} value={d.id}>{d.label}</option>
                ))}
              </select>
              <button
                onClick={() => handleSavePhaseDevice(phase)}
                disabled={updateGrowPhase.isPending}
                className="flex items-center gap-1 text-xs bg-cyan-600 text-white rounded px-2.5 py-1 hover:bg-cyan-700 disabled:opacity-50"
              >
                <CheckIcon className="size-3.5" /> Save
              </button>
              <button
                onClick={() => setEditPhaseId(null)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 px-2 py-1"
              >
                <XMarkIcon className="size-3.5" /> Cancel
              </button>
            </div>
          );
        })()}
      </div>

      {/* Live gauges */}
      {grow.device_id == null ? (
        <div className="bg-white rounded-xl shadow p-4 mb-4 text-sm text-gray-400">No device linked to this grow.</div>
      ) : deviceLoading ? (
        <div className="mb-4"><Spinner /></div>
      ) : device && (
        <div className="bg-white rounded-xl shadow p-4 mb-4">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Live Sensors — {device.label}</h2>
          <div className="flex flex-wrap gap-4">
            {device.sensors.map(sensor => {
              const vals = effectiveValues[sensor.sensor_code] ?? {};
              const tempVal = vals["temperature"] ?? null;
              const humVal  = vals["humidity"]    ?? null;
              const co2Val  = vals["co2"]          ?? null;
              const vpd = (tempVal != null && humVal != null) ? computeVPD(tempVal, humVal) : null;
              const thresholds = device.thresholds;
              return (
                <div key={sensor.id} className="flex gap-3 flex-wrap">
                  {tempVal != null && (
                    <Gauge
                      title="Temp °F"
                      value={tempVal}
                      limitLow={thresholds["temperature"]?.low_value ?? 60}
                      limitHigh={thresholds["temperature"]?.high_value ?? 90}
                      minValue={40}
                      maxValue={100}
                    />
                  )}
                  {humVal != null && (
                    <Gauge
                      title="Humidity %"
                      value={humVal}
                      limitLow={thresholds["humidity"]?.low_value ?? 60}
                      limitHigh={thresholds["humidity"]?.high_value ?? 95}
                      minValue={0}
                      maxValue={100}
                    />
                  )}
                  {co2Val != null && (
                    <Gauge
                      title="CO₂ ppm"
                      value={co2Val}
                      limitLow={thresholds["co2"]?.low_value ?? 400}
                      limitHigh={thresholds["co2"]?.high_value ?? 2000}
                      minValue={0}
                      maxValue={12000}
                    />
                  )}
                  {vpd != null && (
                    <Gauge
                      title="VPD kPa"
                      value={vpd}
                      limitLow={0.1}
                      limitHigh={0.9}
                      minValue={0}
                      maxValue={2}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Charts */}
      {device?.hostname && timeRange && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
              Sensor Data — {device.label}
            </h2>
            <div className="flex items-center gap-2">
              {grow.template_grow_id && (
                <GrowReferenceToggle
                  enabled={reference.enabled}
                  onToggle={reference.toggle}
                  isLoading={reference.isLoading}
                  hasError={reference.hasError}
                />
              )}
              <TimeRangePicker
                start={timeRange.start}
                end={timeRange.end}
                onRangeChange={isCompleted ? undefined : handleRangeChange}
                dark={darkMode}
              />
            </div>
          </div>
          {/* Event type toggle chips */}
          {presentTypes.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {presentTypes.map((type) => {
                const meta = EVENT_META[type];
                const active = enabledSet.has(type);
                return (
                  <button
                    key={type}
                    onClick={() => toggleType(type)}
                    className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-all ${
                      active
                        ? "text-white border-transparent"
                        : "bg-white text-gray-400 border-gray-200 opacity-50"
                    }`}
                    style={active ? { backgroundColor: meta.color, borderColor: meta.color } : {}}
                  >
                    {meta.icon} {meta.label}
                  </button>
                );
              })}
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <MetricChart
              title="Temperature"
              metric="temperature"
              hostname={device.hostname}
              unit="°F"
              color="#f97316"
              timeRange={timeRange}
              marks={marks}
              chartGroup="grow-detail"
              sensorLabels={device.sensorLabels}
              extraSeries={reference.referenceSeriesFor("temperature")}
              darkMode={darkMode}
              timeZone={tz}
            />
            <MetricChart
              title="Humidity"
              metric="humidity"
              hostname={device.hostname}
              unit="%"
              color="#3b82f6"
              timeRange={timeRange}
              marks={marks}
              chartGroup="grow-detail"
              sensorLabels={device.sensorLabels}
              extraSeries={reference.referenceSeriesFor("humidity")}
              darkMode={darkMode}
              timeZone={tz}
            />
            <MetricChart
              title="CO₂"
              metric="co2"
              hostname={device.hostname}
              unit="ppm"
              color="#10b981"
              timeRange={timeRange}
              marks={marks}
              chartGroup="grow-detail"
              sensorLabels={device.sensorLabels}
              extraSeries={reference.referenceSeriesFor("co2")}
              darkMode={darkMode}
              timeZone={tz}
            />
            <VPDChart
              hostname={device.hostname}
              timeRange={timeRange}
              marks={marks}
              darkMode={darkMode}
              timeZone={tz}
            />
          </div>
        </div>
      )}

      {/* Info panel */}
      <div className="bg-white rounded-xl shadow p-5">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Details</h2>
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4 text-sm">
          <div>
            <dt className="text-xs text-gray-400 mb-0.5">Started</dt>
            <dd className="text-gray-700 font-medium flex items-center gap-1.5">
              {startedAtEdit ? (
                <span className="flex items-center gap-1.5">
                  <input
                    type="date"
                    value={startedAtValue}
                    onChange={e => setStartedAtValue(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  />
                  <button onClick={handleSaveStartedAt} className="text-cyan-600 hover:text-cyan-800"><CheckIcon className="size-4" /></button>
                  <button onClick={() => { setStartedAtEdit(false); setStartedAtError(null); }} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="size-4" /></button>
                  {startedAtError && <span className="text-xs text-red-600">{startedAtError}</span>}
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  {formatDate(grow.started_at, tz)}
                  <button
                    onClick={() => {
                      setStartedAtEdit(true);
                      setStartedAtValue(new Date(grow.started_at).toISOString().slice(0, 10));
                    }}
                    className="opacity-40 hover:opacity-100 transition-opacity ml-1"
                  >
                    <PencilIcon className="size-3.5" />
                  </button>
                </span>
              )}
            </dd>
          </div>
          {grow.ended_at && (
            <div>
              <dt className="text-xs text-gray-400 mb-0.5">Ended</dt>
              <dd className="text-gray-700 font-medium">{formatDate(grow.ended_at, tz)}</dd>
            </div>
          )}
          {grow.substrate && (
            <div>
              <dt className="text-xs text-gray-400 mb-0.5">Substrate</dt>
              <dd className="text-gray-700 font-medium">{grow.substrate}</dd>
            </div>
          )}
          <div>
            <dt className="text-xs text-gray-400 mb-0.5">Crop type</dt>
            <dd className="text-gray-700 font-medium capitalize">{grow.crop_type}</dd>
          </div>
          {device && (
            <div>
              <dt className="text-xs text-gray-400 mb-0.5">Device</dt>
              <dd>
                <Link
                  to={`/dashboard/${grow.device_id}`}
                  className="text-cyan-600 hover:text-cyan-800 font-medium"
                >
                  {device.label} →
                </Link>
              </dd>
            </div>
          )}
          <div>
            <dt className="text-xs text-gray-400 mb-0.5">Current phase</dt>
            <dd className="text-gray-700 font-medium">{currentPhase?.phase_name ?? "—"}</dd>
          </div>
        </dl>
      </div>

      {/* Phase Sensor Summaries */}
      {phaseSummaries.length > 0 && (
        <div className="bg-white rounded-xl shadow p-5 mt-4">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Phase Sensor Averages</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="text-left pb-2 font-medium">Phase</th>
                  <th className="text-right pb-2 font-medium">Temp °F</th>
                  <th className="text-right pb-2 font-medium">Humidity %</th>
                  <th className="text-right pb-2 font-medium">CO₂ ppm</th>
                  <th className="text-right pb-2 font-medium">Samples</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {phaseSummaries.map(s => {
                  const phase = phases.find(p => p.id === s.phase_id);
                  return (
                    <tr key={s.phase_id} className="text-gray-700">
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-sm shrink-0 ${PHASE_COLORS[phase?.phase_name] ?? "bg-gray-300"}`} />
                          {phase?.phase_name ?? `Phase ${s.phase_id}`}
                        </div>
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {s.mean_temp_f != null ? (
                          <span title={`min ${s.min_temp_f?.toFixed(1)} / max ${s.max_temp_f?.toFixed(1)}`}>
                            <span className="font-medium">{s.mean_temp_f.toFixed(1)}</span>
                            <span className="text-xs text-gray-400 ml-1">({s.min_temp_f?.toFixed(1)}–{s.max_temp_f?.toFixed(1)})</span>
                          </span>
                        ) : "—"}
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {s.mean_humidity != null ? (
                          <span title={`min ${s.min_humidity?.toFixed(1)} / max ${s.max_humidity?.toFixed(1)}`}>
                            <span className="font-medium">{s.mean_humidity.toFixed(1)}</span>
                            <span className="text-xs text-gray-400 ml-1">({s.min_humidity?.toFixed(1)}–{s.max_humidity?.toFixed(1)})</span>
                          </span>
                        ) : "—"}
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {s.mean_co2_ppm != null ? (
                          <span title={`min ${s.min_co2_ppm?.toFixed(0)} / max ${s.max_co2_ppm?.toFixed(0)}`}>
                            <span className="font-medium">{s.mean_co2_ppm.toFixed(0)}</span>
                            <span className="text-xs text-gray-400 ml-1">({s.min_co2_ppm?.toFixed(0)}–{s.max_co2_ppm?.toFixed(0)})</span>
                          </span>
                        ) : "—"}
                      </td>
                      <td className="py-2 text-right tabular-nums text-gray-400">{s.sample_count}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Harvest Log */}
      <div className="mt-4">
        <HarvestLog growId={growId} harvests={harvests} />
      </div>

      {/* Notes & Events */}
      <div className="mt-4">
        <GrowNotesFeed growId={growId} notes={notes} />
      </div>
    </LayoutWide>
  );
}
